import React from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';

import Icon from './Icon';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { mapBy } from '../../../../../../../shared/lib/functional';
import { getLocaleDateStringUTC } from '../../../../../../../shared/lib/time';
import { useEventCache } from '../context/EventCacheContext';
import { useCartCache } from '../context/CartCacheContext';
import {
  slotKeyFromReservationDraft,
  calculateReservationDraftPricing,
  buildReserveTimeslotLabel,
} from '../../../../../../../shared/lib/reserve';
import {
  makeFullName,
  safeIdCompare,
  formatCentsToUSD,
} from '../../../../../../../shared/lib/utils';
import Button from './Button';
import WideImage from './WideImage';
import { WorkingReservationDraft } from '../../../../../../../shared/types/cartSession';
import { evaluateAudienceZone } from '../../../../../../../shared/lib/audience';

export type SelectedPkg = { id: string; name: string; price: string | number };
export type SelectedAddon = { id: string; name: string; price: string | number; qty: number };

export interface PotentialDraft {
  slotKey: string;
  ticketQuantities: Record<string, number>;
}

export interface ExperienceSummaryProps {
  nextLabel?: string;
  nextIcon?: IconProp | null;
  nextIconPosition?: 'prefix' | 'suffix';
  nextDisabled?: boolean;
  onNext?: () => void;

  backLabel?: string;
  onBack?: () => void;

  loading?: boolean;
  error?: string;
  potentialDraft?: PotentialDraft;
  draftOverride?: WorkingReservationDraft;
  showDisclaimer?: boolean;
  footerButtons?: React.ReactNode;
  titleButtons?: React.ReactNode;
  noImage?: boolean;
}

export default function ExperienceSummary(props: ExperienceSummaryProps) {
  const {
    draftOverride,
    potentialDraft,
    showDisclaimer,
    onBack,
    onNext,
    nextDisabled,
    nextLabel,
    backLabel,
    noImage,
    titleButtons,
    footerButtons,
    loading,
    error,
  } = props;

  const { event, allEvents } = useEventCache();
  const { activeDraft: draftFromCache } = useCartCache();
  const activeDraft = draftOverride ?? draftFromCache;

  const isNewSession = draftOverride
    ? false
    : event
      ? activeDraft?.reservationDraft.eventSnapshot.eventTemplate !== event.eventTemplate?._id
      : false;

  const draft = isNewSession ? undefined : activeDraft?.reservationDraft;
  const draftTemplateId = draft?.eventTemplate ?? event?.eventTemplate._id;

  // after the dust settles, use this one source of truth for the template to display
  const displayTemplate =
    allEvents?.eventTemplates?.find(et => et._id === draftTemplateId) ?? event?.eventTemplate;

  const slotKey = isNewSession
    ? potentialDraft?.slotKey
    : draft
      ? slotKeyFromReservationDraft(draft)
      : undefined;
  const slotMatch = event?.timeslots.find(ts => ts.slotKey === slotKey);

  const mySlotDate = slotMatch?.date
    ? new Date(slotMatch.date)
    : draft?.date
      ? new Date(draft.date)
      : undefined;
  const mySlotTime = slotMatch?.time ?? draft?.time;
  const mySlotHostId = slotMatch?.host ?? draft?.host;
  const mySlotRentalId = slotMatch?.rental ?? draft?.rental;
  const mySlotDuration = slotMatch?.duration ?? draft?.duration;
  const mySlotLabel = slotMatch?.label;
  const mySlotTimeDisplay = slotMatch?.timeDisplay;

  let resolvedHostLabel = '';
  let resolvedRentalLabel = '';
  let resolvedTimeLabel = mySlotTime
    ? buildReserveTimeslotLabel({
        label: mySlotLabel,
        time: mySlotTime,
        timeDisplay: mySlotTimeDisplay,
        duration: mySlotDuration,
        dayspan: slotMatch?.dayspan,
      })
    : '';

  if (mySlotHostId) {
    const hosts = event?.collections?.hosts ?? [];
    const foundHost = hosts.find(h => safeIdCompare(h._id, mySlotHostId));

    if (foundHost) {
      resolvedHostLabel = makeFullName(foundHost.firstName, foundHost.lastName);
    }
  }

  const availableTickets = displayTemplate?.reservables.tickets ?? [];
  const ticketsMap = mapBy(availableTickets, '_id');

  const myTickets = isNewSession
    ? Object.entries(potentialDraft?.ticketQuantities ?? {})
        .map(([ticketId, quantity]) => ({
          ticket: ticketsMap[ticketId],
          quantity,
        }))
        .filter(t => t.quantity > 0)
    : (draft?.tickets ?? []);
  const myPackages = isNewSession ? [] : (draft?.packages ?? []);
  const myAddons = isNewSession ? [] : (draft?.addons ?? []);

  if (mySlotRentalId) {
    const rental = ticketsMap[mySlotRentalId];
    if (rental) {
      resolvedRentalLabel = rental.name;
    }
  }

  const hasTickets = myTickets.length > 0;
  const hasPackages = myPackages.length > 0;
  const hasAddons = myAddons.length > 0;

  const startingTicket = availableTickets[0];
  const startingPriceLabel = startingTicket
    ? `From ${formatCentsToUSD(startingTicket?.price ?? 0)}`
    : 'Pricing available';

  const pricing = draft ? calculateReservationDraftPricing([draft]) : undefined;

  const hasFees = (pricing?.feeLineItems ?? []).length > 0;

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, displayTemplate?.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="rounded-lg border border-border overflow-hidden">
        {!noImage && (
          <WideImage
            image={displayTemplate?.images[0]}
            title={displayTemplate?.title ?? 'Experience image'}
            className="rounded-0-i"
          />
        )}
        <div className="grid grid-cols-1 gap-6 p-6">
          {displayTemplate?.title && (
            <div className="flex flex-wrap items-center gap-4">
              <div>
                {(visibleZones ?? []).length > 0 ? (
                  <div className="flex flex-wrap gap-1 mb-1 go-event-zones">{visibleZones}</div>
                ) : null}
                <div className="font-bold text-lg text-accent">{displayTemplate.title}</div>
              </div>
              {titleButtons && (
                <>
                  <div className="ml-auto"></div>
                  {titleButtons}
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-1 text-secondary">
            {error && <div className="text-error font-semibold">{error}</div>}

            <Icon
              icon={['fass', 'ticket']}
              text={displayTemplate?.reservables.marketingPrice || startingPriceLabel}
            />

            {mySlotDate && (
              <Icon
                icon={['fass', 'calendar']}
                text={getLocaleDateStringUTC(mySlotDate, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              />
            )}

            {mySlotTime ? (
              <Icon icon={['fass', 'clock']} text={resolvedTimeLabel} />
            ) : (
              <Icon className="text-error" icon={['fass', 'clock']} text="Slot not selected" />
            )}

            {resolvedHostLabel && <Icon icon={['fass', 'user']} text={resolvedHostLabel} />}

            {resolvedRentalLabel && (
              <Icon icon={['fass', 'diamonds-4']} text={resolvedRentalLabel} />
            )}

            {displayTemplate?.reservables.marketingDuration && (
              <Icon icon={['fass', 'clock']} text={displayTemplate.reservables.marketingDuration} />
            )}

            {displayTemplate?.reservables.marketingCapacity && (
              <Icon
                icon={['fass', 'user-group-simple']}
                text={displayTemplate.reservables.marketingCapacity}
              />
            )}
          </div>
          {pricing && (
            <>
              {hasTickets && (
                <>
                  <hr className="border-t border-border -mx-6" />
                  <div className="grid grid-cols-1 gap-2 text-base font-bold">
                    {pricing.ticketLineItems.map((t, i) => (
                      <div key={i} className="flex items-baseline justify-between">
                        <div className="mr-3">
                          {t.label}
                          {t.quantity > 1 && <span>&nbsp;x{t.quantity}</span>}
                        </div>
                        <div>{formatCentsToUSD(t.price)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {hasPackages && (
                <>
                  <hr className="border-t border-border -mx-6" />
                  <div className="grid grid-cols-1 gap-2 text-base font-bold">
                    {pricing.packageLineItems.map((p, i) => (
                      <div key={i} className="flex items-baseline justify-between">
                        <div className="mr-3">
                          {p.label}
                          {p.quantity > 1 && <span>&nbsp; x{p.quantity}</span>}
                        </div>
                        <div>{formatCentsToUSD(p.price)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {hasAddons && (
                <>
                  <hr className="border-t border-border -mx-6" />
                  <div className="grid grid-cols-1 gap-2 text-base font-bold">
                    {pricing.addonLineItems.map((a, i) => (
                      <div key={i} className="flex items-baseline justify-between">
                        <div className="mr-3">
                          {a.label}
                          {a.quantity > 1 && <span>&nbsp; x{a.quantity}</span>}
                        </div>
                        <div>{formatCentsToUSD(a.price)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {hasFees && (
                <>
                  <hr className="border-t border-border -mx-6" />
                  <div className="grid grid-cols-1 gap-2 text-base font-bold">
                    {pricing.feeLineItems.map((f, i) => (
                      <div key={i} className="flex items-baseline justify-between">
                        <div className="mr-3">{f.label}</div>
                        <div>{formatCentsToUSD(f.price)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <hr className="border-t border-border -mx-6" />
              <div className="flex items-baseline justify-between text-lg font-bold text-accent">
                <div className="mr-3">Subtotal</div>
                <div>{formatCentsToUSD(pricing.finalTotal)}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {footerButtons ? (
        footerButtons
      ) : onBack || onNext ? (
        <div className="flex flex-wrap items-center gap-2">
          {onBack && (
            <Button
              variant="borderlike"
              onClick={onBack}
              className="shrink-0"
              icon={['fass', 'arrow-left']}
              loading={loading}
            >
              {backLabel ?? 'Back'}
            </Button>
          )}
          {onNext && (
            <Button
              variant="active"
              onClick={onNext}
              className="flex-grow"
              iconSuffix={['fass', 'arrow-right']}
              disabled={!!nextDisabled}
              loading={loading}
            >
              {nextLabel ?? 'Next'}
            </Button>
          )}
        </div>
      ) : null}

      {showDisclaimer && displayTemplate?.reservables.disclaimer && (
        <div
          className="html-view"
          dangerouslySetInnerHTML={{
            __html: displayTemplate.reservables.disclaimer,
          }}
        />
      )}
    </div>
  );
}
